import integrationEsMx from '../locales/es-MX/integration.json';

/**
 * Wire shape extendido Fiado. Aplica a TODO response del rbac (success y error).
 * REPO_SPEC v1.3 §7. DEC-002.
 */

// DEC-002: wire shape extendido Fiado para success Y error. Ver DECISIONES.md.
export interface ApiSuccessBody<T> {
    code: string;
    date: string;
    data: T;
    description?: string;
    msg?: string;
    encrypted?: boolean;
}

export interface ApiErrorBody {
    code: string;
    date: string;
    data?: unknown;
    /** ⚠️ El wire lo declara string pero varios endpoints mandan `null`. Ver `messageOf`. */
    description?: string | null;
    /** ⚠️ Idem: obligatorio en el contrato, `null` en la realidad. */
    msg: string | null;
    encrypted?: boolean;
}

/** Genérico para fallos del servidor: nunca se muestra el `msg` técnico de un 5xx. */
export const INTERNAL_ERROR_MESSAGE = 'Algo falló de nuestro lado. Intenta de nuevo en un momento.';

/**
 * Qué decirle al usuario cuando el silo NO manda `msg`.
 *
 * 🔴 El wire declara `msg: string` obligatorio, pero varios endpoints responden `msg: null` y
 * `description: null` — el sobre viaja con el `code` puesto y nada más. `super(null)` produce el
 * string `"null"`, así que la pantalla terminaba diciéndole **«null»** al operador.
 *
 * El `code` ES el contrato con el front (el status solo agrupa por familia), así que de ahí sale el
 * mensaje. Los códigos que no estén acá caen a su propio nombre: feo pero VERDADERO, y accionable
 * para reportarlo. Nunca se inventa una causa que no sabemos.
 */
const MESSAGE_BY_CODE: Record<string, string> = {
    PERMISSION_DENIED:
        'No tienes permiso para ver esto. Si crees que es un error, contacta al administrador.',
    SESSION_EXPIRED: 'Tu sesión expiró. Inicia sesión de nuevo.',
    UNAUTHORIZED: 'Necesitas iniciar sesión para continuar.',
    NOT_FOUND: 'No encontramos lo que buscas.',
    VALIDATION_ERROR: 'Hay datos inválidos en la solicitud.',
    INTERNAL_ERROR: INTERNAL_ERROR_MESSAGE,
    SERVICE_UNAVAILABLE: 'El servicio no está disponible en este momento.',
};

/** Códigos cuyo mensaje decide el front aunque el silo mande el suyo. */
const FRONT_MESSAGE_BY_CODE: Record<string, string> = {
    ENTITY_MANAGED_BY_INTEGRATION: integrationEsMx.managedByIntegration,
};

/** El mejor mensaje disponible, en orden de confiabilidad. Jamás devuelve `null` ni `undefined`. */
export function messageOf(body: Pick<ApiErrorBody, 'code' | 'msg' | 'description'>): string {
    if (body.code && Object.hasOwn(FRONT_MESSAGE_BY_CODE, body.code)) {
        return FRONT_MESSAGE_BY_CODE[body.code];
    }
    if (typeof body.msg === 'string' && body.msg.trim()) return body.msg;
    if (typeof body.description === 'string' && body.description.trim()) return body.description;
    // `Object.hasOwn` y no `MESSAGE_BY_CODE[code]`: un `code` como `constructor` acierta contra el
    // prototipo de Object y devolvía el miembro heredado en vez del mensaje.
    if (body.code && Object.hasOwn(MESSAGE_BY_CODE, body.code)) return MESSAGE_BY_CODE[body.code];
    if (body.code) return body.code;
    return 'Algo salió mal, intenta de nuevo';
}

export class ApiError extends Error {
    readonly code: string;
    readonly httpStatus: number;
    readonly msg: string;
    readonly data?: unknown;
    readonly description?: string;
    readonly date: string;
    readonly encrypted: boolean;

    constructor(httpStatus: number, body: ApiErrorBody) {
        super(messageOf(body));
        this.name = 'ApiError';
        this.httpStatus = httpStatus;
        this.code = body.code;
        this.msg = messageOf(body);
        this.data = body.data;
        this.description = body.description;
        this.date = body.date;
        this.encrypted = body.encrypted === true;
    }
}

export class SessionExpiredError extends ApiError {
    constructor(body: ApiErrorBody) {
        super(401, body);
        this.name = 'SessionExpiredError';
    }
}

/**
 * La respuesta llegó con una forma que el portal no sabe leer. NO es «no hay resultados»: o el
 * contrato con el silo cambió, o la respuesta ni siquiera fue JSON.
 */
export class UnexpectedShapeError extends Error {
    readonly endpoint: string;

    constructor(endpoint: string, detail: string) {
        super(`El portal no reconoce la respuesta de ${endpoint}: ${detail}`);
        this.name = 'UnexpectedShapeError';
        this.endpoint = endpoint;
    }
}

export async function parseErrorResponse(response: Response): Promise<ApiError> {
    let body: ApiErrorBody;
    try {
        body = (await response.json()) as ApiErrorBody;
    } catch {
        body = {
            code: 'UNKNOWN_ERROR',
            date: new Date().toISOString(),
            msg: 'Algo salió mal, intenta de nuevo',
        };
    }
    if (response.status === 401 && body.code === 'SESSION_EXPIRED') {
        return new SessionExpiredError(body);
    }
    return new ApiError(response.status, body);
}

/**
 * El mensaje que se le puede ENSEÑAR a un operador, venga de donde venga el error.
 *
 * 🔴 Las pantallas hacían `error instanceof Error ? error.message : …` y con eso un `TypeError` del
 * propio front llegaba crudo y en inglés a la cara del usuario: *«Cannot read properties of
 * undefined (reading 'ranges')»*. Un error de la API sí tiene un mensaje pensado para leerse; uno de
 * programación no, y mostrarlo solo consigue que el operador reporte una frase que no entiende.
 *
 * El detalle técnico no se pierde: sigue en la consola y en Sentry, que es donde sirve.
 */
export function userMessageOf(error: unknown): string {
    if (error instanceof ApiError) return error.message;
    // Por `name` y no por `instanceof`: `RequestTimeoutError` vive en `client.ts`, que importa este
    // archivo — importarlo de vuelta cerraría un ciclo por un solo chequeo.
    if (error instanceof Error && error.name === 'RequestTimeoutError') return error.message;
    return 'Algo falló al cargar la información. Intenta de nuevo; si sigue pasando, repórtalo.';
}
